import {
  LuUsers,
  LuCheck,
  LuClock,
  LuX,
  LuDownload,
  LuSearch,
  LuFilter,
  LuActivity,
  LuLoader,
  LuDollarSign,
  LuFileSpreadsheet,
  LuPhone,
  LuMail
} from "react-icons/lu";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useState, useEffect, useMemo } from "react";
import { db } from "@lib/firebase";
import { collection, getDocs, query, orderBy, limit, startAfter, where, endBefore, limitToLast, getCountFromServer, doc, getDoc } from "firebase/firestore";
import axios from "axios";
import { InputField, ConfirmModal, Button } from "@cakrawalajakarta/shared-components";
import { useAuth } from "../../context/AuthContext";

// Helper to capitalize first letter for search
const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const getRelayTeamMembers = (participant = {}) => {
  const relayMembers = Array.isArray(participant.relayMembers)
    ? participant.relayMembers
    : [];

  if (!relayMembers.length) return [];

  return [
    {
      namaLengkap: participant.namaLengkap || participant.teamName || "-",
      bibName: participant.bibName || "-",
      gender: participant.gender || "-",
      telepon: participant.telepon || "-",
      sizeJersey: participant.sizeJersey || "-",
      golonganDarah: participant.golonganDarah || "-",
      role: "Participant 1",
    },
    ...relayMembers.map((member, index) => ({
      ...member,
      role: `Participant ${index + 2}`,
    })),
  ];
};

const formatRelayMemberNames = (participant = {}) => {
  return getRelayTeamMembers(participant)
    .map((member) => member.namaLengkap)
    .filter(Boolean)
    .join(" / ");
};

export const Participants = () => {
  const { user, getIdToken } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [expandedRow, setExpandedRow] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [isLastPage, setIsLastPage] = useState(false);
  const [itemsPerPage] = useState(50); // Increased to 50 as per plan
  const [page, setPage] = useState(1);

  const [processing, setProcessing] = useState(null);
  const [pendingUpgrades, setPendingUpgrades] = useState([]);
  const [upgradeParticipants, setUpgradeParticipants] = useState([]);
  const [upgradeProcessing, setUpgradeProcessing] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null, action: null, message: "" });
  const [certModal, setCertModal] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [bibTransferModal, setBibTransferModal] = useState({
    isOpen: false,
    sender: null,
    receiverSearch: "",
    suggestions: [],
    selectedReceiver: null,
    saving: false,
  });

  const COLLECTION_NAME = "pendaftaranUltra24";
  const SPREADSHEET_ID = "1R1i2mX9qFjRuKL-c-7fbcCBcmhBg8EBNX2VjwThMFuc";
  
  // Date for header
  const currentDate = new Date().toLocaleDateString('id-ID', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const [stats, setStats] = useState({
    total: 0,
    settlement: 0,
    qualified: 0,
    pending: 0,
    rejected: 0,
    waitingApproval: 0
  });

  useEffect(() => {
    fetchParticipants("init");
    fetchStats();
    fetchPendingUpgrades();
  }, [approvalFilter, paymentFilter, categoryFilter]);

  const fetchPendingUpgrades = async () => {
    try {
      // Approved-but-unpaid counts as open: the category has not moved yet, so
      // the row must keep showing the pending change. Only the action buttons
      // are gated on approval_status further down.
      const snapshot = await getDocs(
        query(
          collection(db, "categoryTransferPayments"),
          where("approval_status", "in", ["pending_approval", "approved"])
        )
      );
      const upgrades = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) =>
          u.eventId === "ULTRA24" &&
          !u.applied &&
          !["settlement", "capture"].includes(u.transaction_status)
        );
      setPendingUpgrades(upgrades);

      // These participants may live on any page of the paginated list, so fetch
      // their rows directly. Without this there is nothing on screen to sort up.
      const sourceIds = [...new Set(upgrades.map((u) => u.sourceOrderId).filter(Boolean))];
      const rows = await Promise.all(
        sourceIds.map(async (orderId) => {
          try {
            const snap = await getDoc(doc(db, COLLECTION_NAME, orderId));
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
          } catch {
            return null;
          }
        })
      );
      setUpgradeParticipants(rows.filter(Boolean));
    } catch (err) {
      console.error("Error fetching pending upgrades:", err);
    }
  };

  const handleUpgradeReview = async (transferOrderId, action) => {
    setUpgradeProcessing(transferOrderId);
    try {
      const token = await getIdToken();
      await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/category-transfer/approve-upgrade/${transferOrderId}`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Upgrade ${action === "approve" ? "approved" : "rejected"} successfully.`);
      fetchPendingUpgrades();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to process upgrade.");
    } finally {
      setUpgradeProcessing(null);
    }
  };

  // Separate function for fetching stats to keep main fetch light
  const fetchStats = async () => {
    try {
        const coll = collection(db, COLLECTION_NAME);
        
        // 1. Total
        const totalSnap = await getCountFromServer(coll);
        
        // 2. Settlement
        const settlementQuery = query(coll, where("transaction_status", "==", "settlement"));
        const settlementSnap = await getCountFromServer(settlementQuery);

        // A transferred-out entry is still "settlement" but is no longer an
        // active participant — the receiver is counted instead. Without this
        // subtraction one paid slot would be counted twice.
        const transferredOutSnap = await getCountFromServer(
          query(coll, where("bibTransfer.transferredOut", "==", true))
        );

        // 3. Qualified (Approved)
        const qualifiedQuery = query(coll, where("administration_status", "==", "qualified"));
        const qualifiedSnap = await getCountFromServer(qualifiedQuery);

        // 4. Rejected
        const rejectedQuery = query(coll, where("administration_status", "==", "rejected"));
        const rejectedSnap = await getCountFromServer(rejectedQuery);
        
        // 5. Pending (Waiting Approval) - logic: status is pending OR undefined, AND not rejected/qualified
        // This is hard to query exactly in one go. We can approximate:
        // Or simply query "administration_status == pending" if we are consistent.
        // Let's rely on what we can query accurately.
        // Assuming "pending" is explicitly set.
        const pendingAdminQuery = query(coll, where("administration_status", "==", "pending"));
        const pendingAdminSnap = await getCountFromServer(pendingAdminQuery);
        
        setStats({
            total: totalSnap.data().count,
            settlement: settlementSnap.data().count - transferredOutSnap.data().count,
            qualified: qualifiedSnap.data().count,
            rejected: rejectedSnap.data().count,
            waitingApproval: pendingAdminSnap.data().count, // Approximation
            pending: 0 // If needed
        });
    } catch (err) {
        console.error("Error fetching stats:", err);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
        if(searchQuery !== "") fetchParticipants("search");
        else fetchParticipants("init");
    }, 800);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const enrichWithAvatars = async (list) => {
    const uids = [...new Set(list.map(p => p.uid).filter(Boolean))];
    if (!uids.length) return list;
    const chunks = [];
    for (let i = 0; i < uids.length; i += 30) chunks.push(uids.slice(i, i + 30));
    const photoMap = {};
    await Promise.all(
      chunks.flatMap(chunk =>
        chunk.map(async uid => {
          try {
            const snap = await getDoc(doc(db, "users", uid));
            if (snap.exists()) photoMap[uid] = snap.data().photoURL || snap.data().photoUrl || null;
          } catch {}
        })
      )
    );
    return list.map(p => ({ ...p, photoURL: photoMap[p.uid] || p.photoURL || p.photoUrl || null }));
  };

  const fetchParticipants = async (action = "init") => {
    setLoading(true);
    try {
      let q = collection(db, COLLECTION_NAME);
      const constraints = [];

      // 1. Apply Filters
      if (approvalFilter !== "all") {
         if (approvalFilter === "waiting") {
             // Waiting: administration_status is "pending" or undefined, and not qualified/rejected
             // Query for pending status (most common case)
             constraints.push(where("administration_status", "==", "pending"));
         } else if (approvalFilter === "approved") {
             constraints.push(where("administration_status", "==", "qualified"));
             constraints.push(where("transaction_status", "==", null)); // Server-side filter for hasn't made transaction
         } else if (approvalFilter === "rejected") {
             constraints.push(where("administration_status", "==", "rejected"));
         }
      }
      
      if (paymentFilter !== "all") {
          // Fix: Map UI value "expired" to DB value "expire"
          const dbStatus = paymentFilter === "expired" ? "expire" : paymentFilter;
          constraints.push(where("transaction_status", "==", dbStatus));
      }

      if (categoryFilter !== "all") {
          constraints.push(where("kategori", "==", categoryFilter));
      }

      // 2. Search Logic (Overrides Sort)
      if (action === "search" && searchQuery) {
          const term = searchQuery.trim();

          if (term.toUpperCase().startsWith("ORDER-")) {
              // Exact Order ID Search
              constraints.push(where("orderId", "==", term));
          } else if (term.includes("@")) {
              // Email Search
              constraints.push(where("email", "==", term));
          } else {
              // Semua kata kunci lain (BIB number, nama lengkap, BIB name,
              // telepon, NIK) dicari lewat searchKeywords: array prefix yang
              // dibuat saat pendaftaran dan di-backfill dengan BIB.
              // Ini membuat pencarian tidak lagi harus cocok dari awal nama
              // dan tidak peduli huruf besar/kecil.
              constraints.push(where("searchKeywords", "array-contains", term.toLowerCase()));
          }
      } else {
          // Default Sort (Only if not searching)
          // If approval filter is active, sort by administration_status first, then createdAt
          if (approvalFilter !== "all") {
              constraints.push(orderBy("administration_status"));
              constraints.push(orderBy("createdAt", "desc"));
          } else {
              constraints.push(orderBy("createdAt", "desc"));
          }
      }

      // 3. Pagination Logic
      if (action === "next" && lastVisible) {
          constraints.push(startAfter(lastVisible));
          constraints.push(limit(itemsPerPage));
      } else if (action === "prev" && firstVisible) {
          constraints.push(endBefore(firstVisible));
          constraints.push(limitToLast(itemsPerPage));
      } else {
          // Init or Search
          constraints.push(limit(itemsPerPage));
      }
      
      const finalQuery = query(q, ...constraints);
      const snapshot = await getDocs(finalQuery);
      
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const displayList = await enrichWithAvatars(list);
      
      if (snapshot.docs.length > 0) {
        setFirstVisible(snapshot.docs[0]);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      } else {
        if(action === 'next') setIsLastPage(true);
      }

      // No more client-side filtering needed, completely server-side

      if (action === 'next') {
          setParticipants(prev => [...prev, ...displayList]);
      } else {
          setParticipants(displayList);
      }
      
      if(action === 'init' || action === 'search') {
          setIsLastPage(snapshot.empty || snapshot.docs.length < itemsPerPage);
      } else if (action === 'next') {
          setIsLastPage(snapshot.empty || snapshot.docs.length < itemsPerPage);
      }

    } catch (error) {
      console.error("Error fetching participants:", error);
      // alert("Gagal memuat data: " + error.message); // Silent fail is better for search
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (participant) => {
    setProcessing(participant.id);
    try {
        await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/approve-registration`, {
            orderId: participant.orderId,
            eventId: participant.eventId || "ULTRA24",
            approvedBy: user?.name || user?.displayName || user?.email || "Unknown Admin"
        }, {
            headers: {
                Authorization: `Bearer ${await getIdToken()}`
            }
        });
        
        // Optimistic update
        setParticipants(prev => prev.map(p => 
            p.id === participant.id 
                ? { ...p, administration_status: "qualified", approvedBy: user?.name || user?.displayName || user?.email || "Unknown Admin" } 
                : p
        ));
    } catch (error) {
        console.error("Approval failed:", error);
        alert("Failed to approve participant: " + (error.response?.data?.message || error.message));
    } finally {
        setProcessing(null);
    }
  };

  const handleReject = async (participant) => {
    setProcessing(participant.id);
    try {
      await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/reject-registration`, {
        orderId: participant.orderId,
        eventId: participant.eventId || "ULTRA24",
        rejectedBy: user?.name || user?.displayName || user?.email || "Unknown Admin"
      }, {
        headers: {
            Authorization: `Bearer ${await getIdToken()}`
        }
      });

      setParticipants(prev =>
        prev.map(p =>
          p.id === participant.id
            ? { ...p, administration_status: "rejected", rejectedBy: user?.name || user?.displayName || user?.email || "Unknown Admin" }
            : p
        )
      );
    } catch (error) {
      console.error("Rejection failed:", error);
      alert("Gagal menolak pendaftaran. Silakan coba lagi.");
    } finally {
      setProcessing(null);
    }
  };

  const handleExportPDF = (p) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40, 40, 40);
    doc.text("PARTICIPANT SUMMARY", 105, 20, { align: "center" });
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text("Jakarta Open Trail Run 2026", 105, 26, { align: "center" });

    // Section 1: Participant Details (Neutral Blue Header)
    autoTable(doc, {
        startY: 35,
        head: [['PERSONAL INFORMATION', '']],
        body: [
            ['Full Name', p.namaLengkap],
            ['Bib Name', p.bibName || "-"],
            ['Date of Birth', p.tanggalLahir || "-"],
            ['Address', `${p.alamat || "-"}`],
            ['City/Province', `${p.kota || "-"}, ${p.provinsi || "-"}`],
            ['Country', p.negara || "-"],
            ['Phone Number', p.telepon || "-"],
            ['Email', p.email || "-"],
            ['Category', p.kategori || p.categoryName || "-"],
            ['BIB Number', p.bibNumber || "Pending"],
            ['Order ID', p.orderId],
            ['Registration Date', p.createdAt ? new Date(p.createdAt.seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : "-"],
        ],
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 70, fillColor: [248, 250, 252] } },
        styles: { fontSize: 10, cellPadding: 3, lineColor: [226, 232, 240] },
    });

    // Section 2: Medical Information (Red Header - Urgent)
    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [['MEDICAL INFORMATION', '']],
        body: [
            ['Blood Type', p.golonganDarah || "-"],
            ['Medical History', p.riwayatMedis || "None"],
        ],
        theme: 'grid',
        headStyles: { fillColor: [185, 28, 28], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 70, fillColor: [254, 242, 242] } },
        styles: { fontSize: 10, cellPadding: 4, lineColor: [226, 232, 240] },
    });

    // Section 3: Emergency Contact (Neutral Blue Header)
    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 5,
        head: [['EMERGENCY CONTACT', '']],
        body: [
            ['Emergency Contact Name', p.namaKontakDarurat || "-"],
            ['Emergency Contact Phone', p.kontakDarurat || "-"],
            ['Relationship', p.hubunganKontakDarurat || "-"],
        ],
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 70, fillColor: [248, 250, 252] } },
        styles: { fontSize: 10, cellPadding: 4, lineColor: [226, 232, 240] },
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont("helvetica", "italic");
    
    const generatedText = `Generated on ${new Date().toLocaleString('en-GB')} | ULTRA24 Admin System`;
    doc.text(generatedText, 14, finalY);

    if (p.approvedBy) {
        doc.text(`Approved by: ${p.approvedBy}`, 14, finalY + 5);
    } else if (p.rejectedBy) {
        doc.text(`Rejected by: ${p.rejectedBy}`, 14, finalY + 5);
    }

    // Save
    doc.save(`Summary_${p.bibName || 'Participant'}_${p.orderId}.pdf`);
  };


  // Fixed categories for ULTRA24 to prevent filter options from disappearing
  const categories = [
    "24H INDIVIDU",
    "12H INDIVIDU",
    "6H INDIVIDU",
    "RELAY 100K",
    "RELAY 50K",
    "100K INDIVIDU",
    "50K INDIVIDU",
    "42K INDIVIDU",
    "10K FUN RUN",
    "5K FUN RUN"
  ];

  // Client-side filtering replaced by Server-side filtering in fetchParticipants
  // const filteredParticipants = ... removed

  // Pagination calculated server-side now
  // Participants waiting on an admin action float to the top. The Firestore
  // query orders by createdAt, so without this a pending upgrade sits wherever
  // that person registered — page 4 of 7 — and no admin would ever see it.
  const paginatedParticipants = useMemo(() => {
    if (upgradeParticipants.length === 0 || searchQuery) return participants;

    const needsAction = new Set(
      upgradeParticipants.map((p) => p.orderId).filter(Boolean)
    );

    // Drop them from their natural position, then re-add on top, so a request
    // that belongs to someone on page 4 is still the first thing an admin sees.
    const rest = participants.filter((p) => !needsAction.has(p.orderId));
    return [...upgradeParticipants, ...rest];
  }, [participants, upgradeParticipants, searchQuery]);

  // Reset not needed as we refetch on filter change in the main useEffect

  // Stats are now fetched server-side into 'stats' state
  // const stats = useMemo... removed

  const exportToCSV = () => {
    const headers = [
      "Order ID",
      "Name",
      "Email",
      "Phone",
      "Category",
      "Status",
      "Racepack",
      "BIB",
      "Price",
    ];

    const rows = participants.map((p) => [
      p.orderId || "",
      p.namaLengkap || p.teamName || "",
      p.email || "",
      p.telepon || "",
      p.kategori || "",
      p.transaction_status || "",
      p.racepackCollected ? "Yes" : "No",
      p.bibNumber || "",
      p.harga || "",
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `participants_ultra24_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const executeExportToGoogleSheets = async () => {
    setExporting(true);
    try {
        const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/export-google-sheets`, {
            spreadsheetId: SPREADSHEET_ID,
            eventId: "ULTRA24"
        }, {
            headers: {
                Authorization: `Bearer ${await getIdToken()}`
            }
        });
        
        if (response.data.success) {
            alert("Success! Opening Google Sheets...");
            window.open(response.data.url, "_blank");
        } else if (response.data.message) {
            alert(response.data.message);
        }
    } catch (error) {
        console.error("Export to Sheets failed:", error);
        alert("Gagal export ke Google Sheets: " + (error.response?.data?.error || error.message));
    } finally {
        setExporting(false);
    }
  };

  const exportToGoogleSheets = () => {
    setConfirmModal({
      isOpen: true,
      action: "export",
      data: null,
      message: "Existing data in Sheet1 will be overwritten. Are you sure?"
    });
  };

  const handleConfirmAction = () => {
    if (confirmModal.action === "approve") {
      handleApprove(confirmModal.data);
    } else if (confirmModal.action === "reject") {
      handleReject(confirmModal.data);
    } else if (confirmModal.action === "export") {
      executeExportToGoogleSheets();
    }
    setConfirmModal({ isOpen: false, data: null, action: null, message: "" });
  };


  const getParticipantCategoryLabel = (p) => p.kategori || p.categoryName || p.category || "-";

  const handleBibTransferSearch = async (value, sender) => {
    setBibTransferModal((prev) => ({ ...prev, receiverSearch: value, selectedReceiver: null }));
    if (!value.trim() || value.trim().length < 2) {
      setBibTransferModal((prev) => ({ ...prev, suggestions: [] }));
      return;
    }
    try {
      const lower = value.toLowerCase();
      const senderCategoryLabel = getParticipantCategoryLabel(sender);

      const snap = await getDocs(
        query(
          collection(db, COLLECTION_NAME),
          where("searchKeywords", "array-contains", lower),
          limit(50)
        )
      );
      const matches = snap.docs
        .map((d) => ({ id: d.id, eventId: "ULTRA24", ...d.data() }))
        .filter((p) => {
          if (p.administration_status !== "qualified") return false;
          if (p.bibTransfer?.transferredOut) return false;
          const isPaid = p.transaction_status === "settlement" || p.transaction_status === "capture" || p.paypalCapture?.status === "COMPLETED";
          if (isPaid) return false;
          if (getParticipantCategoryLabel(p) !== senderCategoryLabel) return false;
          return true;
        })
        .slice(0, 8);
      setBibTransferModal((prev) => ({ ...prev, suggestions: matches }));
    } catch (err) {
      console.error("[BIB_TRANSFER] Receiver search failed", err);
    }
  };

  const handleAdminBibTransfer = async () => {
    const { sender, selectedReceiver } = bibTransferModal;
    if (!sender || !selectedReceiver) return;
    setBibTransferModal((prev) => ({ ...prev, saving: true }));
    try {
      const token = await getIdToken();
      const res = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/bib-transfer/admin-apply`,
        {
          eventId: sender.eventId || "ULTRA24",
          senderOrderId: sender.orderId || sender.id,
          receiverOrderId: selectedReceiver.orderId || selectedReceiver.id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBibTransferModal({ isOpen: false, sender: null, receiverSearch: "", suggestions: [], selectedReceiver: null, saving: false });
      alert(`BIB transferred from ${res.data.senderName} to ${res.data.receiverName} successfully.`);
      fetchParticipants("init");
    } catch (err) {
      alert(err.response?.data?.message || "BIB transfer failed.");
      setBibTransferModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const getParticipantBadge = (p) => {
    // An open upgrade outranks the registration status: the registration is
    // settled, but there is still money outstanding on the category change.
    const upgrade = pendingUpgrades.find((u) => u.sourceOrderId === p.orderId);
    if (upgrade) {
      return (
        <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-amber-100 text-amber-700">
           {upgrade.approval_status === "approved" ? "Upgrade unpaid" : "Upgrade Pending"}
        </span>
      );
    }
    // BIB Transferred Out
    if (p.bibTransfer?.transferredOut === true) {
      const receiverName = p.bibTransfer?.transferredToName
        ? p.bibTransfer.transferredToName.trim().split(/\s+/).map((w) => capitalizeFirstLetter(w.toLowerCase())).join(" ")
        : null;
      return (
        <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
          {receiverName ? `BIB to ${receiverName}` : "BIB Transferred"}
        </span>
      );
    }
    // 1. Rejected
    if (p.administration_status === "rejected") {
      return (
        <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-red-100 text-red-700">
           Rejected
        </span>
      );
    }
    // 2. Paid (Settlement)
    if (p.transaction_status === "settlement" || p.transaction_status === "capture") {
        return (
            <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium  bg-green-100 text-green-700">
               Settlement
            </span>
        );
    }
    // 3. Pending Payment (Blue)
    if (p.transaction_status === "pending") {
        return (
            <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium  bg-blue-100 text-blue-700">
               Pending
            </span>
        );
    }
    // 4. Failed/Expired (Red)
    if (["expire", "cancel", "deny", "failure"].includes(p.transaction_status)) {
        let displayStatus = p.transaction_status;
        if (displayStatus === "expire") displayStatus = "Expired";
        
        return (
            <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-red-100 text-red-700">
               {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
            </span>
        );
    }
    // 5. Approved (Gray) - previously Registered
    if (p.administration_status === "qualified") {
        return (
            <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-slate-100 text-slate-600">
               Approved
            </span>
        );
    }
    // 5. Waiting (Default - Gray)
    return (
        <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium  bg-slate-100 text-slate-600">
           Waiting
        </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  if (loading && participants.length === 0 && searchQuery === "") {
    // Only show full page spinner on INITIAL load (no data, no search)
    // If we have data, we just show the loader overlay or button spinner
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-red-600" />
      </div>
    );
  }

  const extractUrl = (text) => {
    if (!text) return null;
    const match = text.match(/(https?:\/\/[^\s]+)/);
    return match ? match[0] : null;
  };

  return (
    <>
      <div className="space-y-4">
      {/* Header with Dashboard Style */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuUsers size={120} />
         </div>
         <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
               <div>
                  <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                     <LuActivity size={14} /> Administration
                  </p>
                   <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">Participant Management</h1>
                   <p className="text-slate-500 text-sm max-w-xl">
                       Search and manage <strong>ULTRA24</strong> participants.
                   </p>
               </div>
               <div className="flex md:justify-end">
                   <button
                       onClick={exportToGoogleSheets}
                       disabled={exporting}
                       className="w-full md:w-auto inline-flex justify-center items-center gap-2 bg-green-600 text-white px-4 py-3 md:py-2 rounded-xl hover:bg-green-700 transition-colors text-sm font-medium h-fit disabled:opacity-50"
                   >
                       {exporting ? <LuLoader className="animate-spin" /> : <LuFileSpreadsheet className="text-white" />}
                       Google Sheets
                   </button>
               </div>
            </div>
         </div>
      </div>

      {/* Stats with Dashboard Style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 w-full">
        {/* Total Card */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400">
             <LuUsers size={20} />
          </div>
          <div>
             <p className="text-slate-500 text-sm font-medium mb-1">Total Registered</p>
             <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.total}</h3>
             <p className="text-sm text-slate-400 mt-1">All entries</p>
          </div>
        </div>

        {/* Confirmed Card */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400">
             <LuDollarSign size={20} />
          </div>
          <div>
             <p className="text-slate-500 text-sm font-medium mb-1">Confirmed</p>
             <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.settlement}</h3>
             <p className="text-sm text-slate-400 mt-1">Settlement status</p>
          </div>
        </div>

        {/* Approved Card */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative text-slate-800">
          <div className="absolute top-4 right-4 text-slate-400">
             <LuCheck size={20} />
          </div>
          <div>
             <p className="text-slate-500 text-sm font-medium mb-1">Approved</p>
             <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">{stats.qualified}</h3>
             <p className="text-sm text-slate-400 mt-1"> {stats.waitingApproval} Waiting approval</p>
          </div>
        </div>

        {/* Rejected Card */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
          <div className="absolute top-4 right-4 text-slate-400">
             <LuX size={20} />
          </div>
          <div>
             <p className="text-slate-500 text-sm font-medium mb-1">Rejected</p>
             <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.rejected}</h3>
             <p className="text-sm text-slate-400 mt-1">Not qualified</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <InputField
                name="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, order ID, or BIB..."
                startIcon={<LuSearch className="text-slate-400" />}
                labelClassName="sr-only"
            />
          </div>

          {/* Approval Filter */}
          <div className="min-w-[180px]">
            <InputField
                type="select"
                name="approvalFilter"
                value={approvalFilter}
                onChange={(e) => setApprovalFilter(e.target.value)}
                options={[
                    { value: "all", label: "All Approvals" },
                    { value: "waiting", label: "Waiting Approval" },
                    { value: "approved", label: "Approved" },
                    { value: "rejected", label: "Rejected" },
                ]}
                startIcon={<LuFilter className="text-slate-400" />}
                defaultOptionLabel="Approval Status"
                labelClassName="sr-only"
            />
          </div>

          {/* Payment Filter */}
          <div className="min-w-[180px]">
            <InputField
                type="select"
                name="paymentFilter"
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                options={[
                    { value: "all", label: "All Payments" },
                    { value: "settlement", label: "Paid (Settlement)" },
                    { value: "pending", label: "Pending Payment" },
                    { value: "expired", label: "Expired/Failed" },
                ]}
                startIcon={<LuDollarSign className="text-slate-400" />}
                defaultOptionLabel="Payment Status"
                labelClassName="sr-only"
            />
          </div>

          {/* Category Filter */}
          <div className="min-w-[200px]">
             <InputField
                type="select"
                name="categoryFilter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                options={[
                    { value: "all", label: "All Categories" },
                    ...categories.map(cat => ({ value: cat, label: cat }))
                ]}
                defaultOptionLabel="Filter Category"
                labelClassName="sr-only"
            />
          </div>
        </div>
      </div>

        <p className="text-sm text-slate-500">
          Showing <span className="font-semibold text-slate-700">{participants.length}</span> items
          {/* Note: Total count is expensive to get in Firestore. We omit "of Total" for now or maintain a counter separately */}
        </p>

      {/* Participant Cards */}
      <div className="flex flex-col gap-2">
        {paginatedParticipants.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <p className="text-slate-500">No participants found</p>
          </div>
        ) : (
          paginatedParticipants.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setExpandedRow(expandedRow === p.id ? null : p.id)}>
                
                {/* Mobile Card View (Redesigned for better hierarchy) */}
                <div className="lg:hidden flex flex-col gap-3 p-1">
                   {/* Top Row: Name and Badges */}
                   <div className="flex flex-col gap-1.5">
                      <h3 className="font-bold text-slate-900 text-[15px] leading-tight truncate">{p.namaLengkap || p.teamName || "-"}</h3>
                      <div className="flex justify-between items-center gap-2">
                         {(() => {
                           const upgrade = pendingUpgrades.find(u => u.sourceOrderId === p.orderId);
                           return (
                             <span className="inline-block bg-slate-800 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                                 {p.kategori || p.categoryName || "-"}
                                 {upgrade && ` → ${upgrade.toCategory || "-"}`}
                             </span>
                           );
                         })()}
                         {getParticipantBadge(p)}
                      </div>
                   </div>

                   {/* Middle Row: Contact & Team */}
                   <div className="flex flex-col gap-1 mt-1">
                       <p className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                          <LuMail size={12} className="text-slate-400 flex-shrink-0"/> {p.email}
                       </p>
                       <p className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                          <LuPhone size={12} className="text-slate-400 flex-shrink-0"/> {p.telepon || "-"}
                       </p>
                       
                       {formatRelayMemberNames(p) && (
                         <div className="mt-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Team Members</p>
                           <p className="text-xs text-slate-600 leading-relaxed" title={formatRelayMemberNames(p)}>
                             {formatRelayMemberNames(p)}
                           </p>
                         </div>
                       )}
                   </div>

                   {/* Stats Row (Only if has cert or result) */}
                   {(() => {
                     const upgrade = pendingUpgrades.find(u => u.sourceOrderId === p.orderId);
                     const certUrl = upgrade?.certificateUrl || p.certificateUrl;
                     const resultUrl = extractUrl(upgrade?.stravaLink || p.raceResultLink);
                     if (!certUrl && !resultUrl) return null;
                     return (
                     <div className="flex items-center gap-4 mt-1.5">
                         {certUrl && (
                            <a href={certUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-red-600 hover:text-red-700 underline underline-offset-2" onClick={(e) => e.stopPropagation()}>
                                View Certificate
                            </a>
                         )}
                         {resultUrl && (
                            <a href={resultUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-red-600 hover:text-red-700 underline underline-offset-2" onClick={(e) => e.stopPropagation()}>
                                View Result
                            </a>
                         )}
                     </div>
                     );
                   })()}

                   {/* Actions Row */}
                   <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3 mt-1">
                      <div className="text-[10px] text-slate-400 font-medium">
                         {pendingUpgrades.find(u => u.sourceOrderId === p.orderId) ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                               Upgrade Pending
                            </span>
                         ) : (
                            <>
                               {p.administration_status === "rejected" && `Rejected by ${p.rejectedBy ? p.rejectedBy.split(' ')[0] : 'Admin'}`}
                               {p.administration_status === "qualified" && `Approved by ${p.approvedBy ? p.approvedBy.split(' ')[0] : 'Admin'}`}
                            </>
                         )}
                      </div>
                      <div className="flex gap-2">
                         {pendingUpgrades.find(u => u.sourceOrderId === p.orderId && u.approval_status === "pending_approval") ? (() => {
                           const upgrade = pendingUpgrades.find(u => u.sourceOrderId === p.orderId && u.approval_status === "pending_approval");
                           return (
                             <>
                               <button onClick={(e) => {e.stopPropagation(); handleUpgradeReview(upgrade.id, "reject");}} disabled={upgradeProcessing === upgrade.id} className="bg-white border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide hover:bg-red-50 transition-colors disabled:opacity-50">Reject</button>
                               <button onClick={(e) => {e.stopPropagation(); handleUpgradeReview(upgrade.id, "approve");}} disabled={upgradeProcessing === upgrade.id} className="bg-slate-900 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide hover:bg-slate-800 transition-colors disabled:opacity-50 border border-slate-900 shadow-sm">{upgradeProcessing === upgrade.id ? "Wait..." : "Approve Upgrade"}</button>
                             </>
                           );
                         })() : ( (p.kategori || "").toLowerCase().includes("fun run") || (p.kategori || "").toLowerCase().includes("relay") ) ? (
                           <span className="text-[10px] text-slate-400">Approved by System</span>
                         ) : p.administration_status !== "rejected" && p.administration_status !== "qualified" && (
                            <>
                               <button onClick={(e) => {e.stopPropagation(); setConfirmModal({isOpen: true, action: 'reject', data: p, message: `Tolak pendaftaran untuk ${p.namaLengkap}? Email notifikasi akan dikirim.`});}} disabled={processing === p.id} className="bg-white border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide hover:bg-red-50 transition-colors disabled:opacity-50">Reject</button>
                               <button onClick={(e) => {e.stopPropagation(); setConfirmModal({isOpen: true, action: 'approve', data: p, message: `Approve registration for ${p.namaLengkap}? This will send a payment link.`});}} disabled={processing === p.id} className="bg-slate-900 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide hover:bg-slate-800 transition-colors disabled:opacity-50 border border-slate-900 shadow-sm">{processing === p.id ? "Wait..." : "Approve"}</button>
                            </>
                         )}
                      </div>
                   </div>
                </div>

                {/* Desktop Row View */}
                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-lime-100 flex items-center justify-center text-slate-700 font-bold flex-shrink-0 overflow-hidden">
                        {(p.photoURL || p.photoUrl) ? (
                          <img src={p.photoURL || p.photoUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                        ) : null}
                        <span style={{ display: (p.photoURL || p.photoUrl) ? 'none' : 'flex' }} className="w-full h-full items-center justify-center">
                          {(p.namaLengkap || p.teamName || "?").charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">{p.namaLengkap || p.teamName || "-"}</p>
                        <p className="text-xs text-slate-500 truncate">{p.email}</p>
                        {formatRelayMemberNames(p) && (
                          <p className="text-xs text-slate-400 truncate" title={formatRelayMemberNames(p)}>
                            {formatRelayMemberNames(p)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    {(() => {
                      const upgrade = pendingUpgrades.find(u => u.sourceOrderId === p.orderId);
                      return (
                        <p className="text-sm font-medium text-slate-800">
                          {p.kategori || p.categoryName || "-"}
                          {upgrade && (
                            <span className="text-amber-600"> → {upgrade.toCategory || "-"}</span>
                          )}
                        </p>
                      );
                    })()}
                  </div>
                  
                  <div className="col-span-1 text-center h-10 flex flex-col justify-start">
                     <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">CERT</p>
                     <div className="flex justify-center leading-none">
                        {(() => {
                           // While an upgrade is pending, the documents that matter are the
                           // ones submitted for it, not the ones from the original signup.
                           const upgrade = pendingUpgrades.find(u => u.sourceOrderId === p.orderId);
                           const certUrl = upgrade?.certificateUrl || p.certificateUrl;
                           return certUrl ? (<button onClick={(e) => { e.stopPropagation(); setCertModal(certUrl); }} className="text-blue-600 font-semibold hover:text-slate-700 text-xs">VIEW</button>) : (<span className="text-xs text-slate-400">-</span>);
                        })()}
                     </div>
                  </div>
                  
                   <div className="col-span-1 text-center h-10 flex flex-col justify-start">
                     <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">RESULT</p>
                     <div className="flex justify-center leading-none">
                        {(() => {
                           const upgrade = pendingUpgrades.find(u => u.sourceOrderId === p.orderId);
                           const validUrl = extractUrl(upgrade?.stravaLink || p.raceResultLink);
                           return validUrl ? (
                               <a href={validUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:text-slate-700 text-xs" onClick={(e) => e.stopPropagation()}>VIEW</a>
                           ) : (
                               <span className="text-xs text-slate-400">-</span>
                           );
                        })()}
                     </div>
                   </div>
                  
                  <div className="col-span-2 text-center">
                    {getParticipantBadge(p)}
                  </div>
                  
                  <div className="col-span-2 flex items-center justify-end gap-2">
                     {pendingUpgrades.find(u => u.sourceOrderId === p.orderId && u.approval_status === "pending_approval") ? (() => {
                       const upgrade = pendingUpgrades.find(u => u.sourceOrderId === p.orderId && u.approval_status === "pending_approval");
                       return (
                         <>
                           <button onClick={(e) => {e.stopPropagation(); handleUpgradeReview(upgrade.id, "approve");}} disabled={upgradeProcessing === upgrade.id} className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50">{upgradeProcessing === upgrade.id ? "..." : "Approve"}</button>
                           <button onClick={(e) => {e.stopPropagation(); handleUpgradeReview(upgrade.id, "reject");}} disabled={upgradeProcessing === upgrade.id} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50">Reject</button>
                         </>
                       );
                     })() : (p.kategori || "").toLowerCase().includes("fun run") ? (
                       <span className="text-xs text-slate-400">Approved by System</span>
                     ) : p.administration_status === "rejected" ? (
                       <span className="text-xs text-red-500 font-medium">Rejected by {p.rejectedBy ? p.rejectedBy.split(' ')[0] : 'Admin'}</span>
                     ) : p.administration_status === "qualified" ? (
                       <span className="text-xs text-slate-400">Approved by {p.approvedBy ? p.approvedBy.split(' ')[0] : 'Admin'}</span>
                     ) : (
                       <><button onClick={(e) => {e.stopPropagation(); setConfirmModal({isOpen: true, action: 'approve', data: p, message: `Approve registration for ${p.namaLengkap}? This will send a payment link.`});}} disabled={processing === p.id} className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50">{processing === p.id ? "..." : "Approve"}</button><button onClick={(e) => {e.stopPropagation(); setConfirmModal({isOpen: true, action: 'reject', data: p, message: `Tolak pendaftaran untuk ${p.namaLengkap}? Email notifikasi akan dikirim.`});}} disabled={processing === p.id} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50">Reject</button></>
                     )}
                   </div>
                </div>

              </div>
              {expandedRow === p.id && (
                <div className="border-t border-slate-200 bg-slate-50 p-4">
                  <div className="space-y-4">
                    {getRelayTeamMembers(p).length > 0 && (
                      <div className="bg-white rounded-xl p-4 border border-slate-200">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-200">Relay Team Members</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                                <th className="py-2 pr-4 font-semibold">Participant</th>
                                <th className="py-2 pr-4 font-semibold">Name</th>
                                <th className="py-2 pr-4 font-semibold">BIB Name</th>
                                <th className="py-2 pr-4 font-semibold">Gender</th>
                                <th className="py-2 pr-4 font-semibold">Phone</th>
                                <th className="py-2 pr-4 font-semibold">Jersey Size</th>
                                <th className="py-2 pr-4 font-semibold">Blood Type</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getRelayTeamMembers(p).map((member, index) => (
                                <tr key={`${member.role}-${member.namaLengkap || index}`} className="border-b border-slate-50 last:border-b-0">
                                  <td className="py-2 pr-4 text-slate-500 whitespace-nowrap">{member.role}</td>
                                  <td className="py-2 pr-4 font-medium text-slate-800 whitespace-nowrap">{member.namaLengkap || "-"}</td>
                                  <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">{member.bibName || "-"}</td>
                                  <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">{member.gender || "-"}</td>
                                  <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">{member.telepon || "-"}</td>
                                  <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">{member.sizeJersey || "-"}</td>
                                  <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">{member.golonganDarah || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-200">Personal Information</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                           <p className="font-mono text-slate-400 text-xs mt-0.5">{p.orderId}</p>
                           {p.midtransOrderId && (
                             <p className="font-mono text-slate-400 text-xs mt-0.5">{p.midtransOrderId}</p>
                           )}
                        </div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Bib Name</p><p className="text-slate-800 text-sm font-medium">{p.bibName || "-"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Gender</p><p className="text-slate-800 text-sm font-medium">{p.gender || "-"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Birth Date</p><p className="text-slate-800 text-sm font-medium">{p.tanggalLahir || "-"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">BIB Number</p><p className="text-slate-800 text-sm font-bold">{p.bibNumber || "Pending"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Phone</p><p className="text-slate-800 text-sm font-medium">{p.telepon || "-"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Jersey Size</p><p className="text-slate-800 text-sm font-medium">{p.sizeJersey || "-"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Registered At</p><p className="text-slate-800 text-sm font-medium">{p.createdAt ? new Date(p.createdAt.seconds * 1000).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : "-"}</p></div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-200">Emergency & Medical</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Emergency Name</p><p className="text-slate-800 text-sm font-medium">{p.namaKontakDarurat || "-"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Emergency Phone</p><p className="text-slate-800 text-sm font-medium">{p.kontakDarurat || "-"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Medical History</p><p className="text-slate-800 text-sm font-medium">{p.riwayatMedis || "None"}</p></div>
                        <div><p className="text-slate-500 text-xs uppercase mb-1">Blood Type</p><p className="text-slate-800 text-sm font-medium">{p.golonganDarah || "-"}</p></div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 justify-start">
                        <Button
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); handleExportPDF(p); }}
                            className="gap-2 shrink-0"
                        >
                            <LuDownload size={16} className="shrink-0" />
                            Export {p.namaLengkap ? p.namaLengkap.split(' ')[0] : 'Data'} (PDF)
                        </Button>
                        {(p.transaction_status === "settlement" || p.transaction_status === "capture" || p.paypalCapture?.status === "COMPLETED") && !p.bibTransfer?.transferredOut && (
                          <Button
                            variant="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBibTransferModal({
                                isOpen: true,
                                sender: p,
                                receiverSearch: "",
                                suggestions: [],
                                selectedReceiver: null,
                                saving: false,
                              });
                            }}
                          >
                            Transfer BIB
                          </Button>
                        )}
                    </div>

                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Load More Button */}
      {!isLastPage && (
          <div className="flex justify-end mt-6">
              <Button 
                onClick={() => fetchParticipants("next")} 
                disabled={loading} 
                variant="secondary"
                className="px-6 py-2 h-auto text-sm font-medium flex items-center gap-2"
              >
                 {loading ? <LuLoader className="animate-spin" /> : null}
                 Load More
              </Button>
          </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, data: null, action: null, message: "" })}
        onConfirm={handleConfirmAction}
        message={confirmModal.message}
        isLoading={exporting || processing === confirmModal.data?.id}
      />
    </div>

    {bibTransferModal.isOpen && bibTransferModal.sender && (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setBibTransferModal({ isOpen: false, sender: null, receiverSearch: "", suggestions: [], selectedReceiver: null, saving: false })} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="text-base font-bold text-slate-800">Transfer BIB</h2>
            <p className="text-xs text-slate-500 mt-0.5">Transfer registration to another approved participant in the same category</p>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold mb-0.5">From</p>
                <p className="font-semibold text-slate-800">{bibTransferModal.sender.namaLengkap || "-"}</p>
                <p className="text-xs text-slate-400">{bibTransferModal.sender.email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold mb-0.5">Category</p>
                <p className="font-semibold text-slate-800">{getParticipantCategoryLabel(bibTransferModal.sender)}</p>
                <p className="text-xs font-mono text-slate-400">{bibTransferModal.sender.orderId || bibTransferModal.sender.id}</p>
              </div>
            </div>
            <div className="relative">
              <InputField
                label="Transfer To"
                required
                labelColor="text-slate-700"
                name="bibReceiver"
                type="text"
                placeholder="Search receiver by name or email..."
                value={bibTransferModal.receiverSearch}
                onChange={(e) => handleBibTransferSearch(e.target.value, bibTransferModal.sender)}
                autoComplete="off"
                startIcon={<LuSearch className="text-slate-400" />}
              />
              {bibTransferModal.suggestions.length > 0 && !bibTransferModal.selectedReceiver && (
                <div className="absolute z-10 top-full left-0 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden mt-1">
                  {bibTransferModal.suggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="w-full text-left px-4 py-2.5 hover:bg-red-50 transition-colors border-b border-slate-50 last:border-0"
                      onClick={() => setBibTransferModal((prev) => ({
                        ...prev,
                        selectedReceiver: s,
                        receiverSearch: s.namaLengkap,
                        suggestions: [],
                      }))}
                    >
                      <p className="text-sm font-semibold text-slate-800">{s.namaLengkap}</p>
                      <p className="text-xs text-slate-400">{s.email} &middot; {s.orderId || s.id}</p>
                    </button>
                  ))}
                </div>
              )}
              {bibTransferModal.suggestions.length === 0 && bibTransferModal.receiverSearch.length >= 2 && !bibTransferModal.selectedReceiver && (
                <p className="mt-1 text-xs text-slate-400">No matching approved participants found in this category.</p>
              )}
            </div>
            {bibTransferModal.selectedReceiver && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">Selected Receiver</p>
                <p className="font-bold text-slate-800">{bibTransferModal.selectedReceiver.namaLengkap}</p>
                <p className="text-xs text-slate-500">{bibTransferModal.selectedReceiver.email} &middot; {bibTransferModal.selectedReceiver.orderId || bibTransferModal.selectedReceiver.id}</p>
                <button
                  type="button"
                  className="mt-1 text-xs text-red-500 hover:underline"
                  onClick={() => setBibTransferModal((prev) => ({ ...prev, selectedReceiver: null, receiverSearch: "" }))}
                >
                  Clear selection
                </button>
              </div>
            )}
            <p className="text-xs text-slate-400">This will permanently transfer the BIB. The sender&apos;s entry will be marked inactive. This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50">
            <Button
              variant="secondary"
              onClick={() => setBibTransferModal({ isOpen: false, sender: null, receiverSearch: "", suggestions: [], selectedReceiver: null, saving: false })}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAdminBibTransfer}
              disabled={bibTransferModal.saving || !bibTransferModal.selectedReceiver}
            >
              {bibTransferModal.saving ? "Transferring..." : "Confirm Transfer"}
            </Button>
          </div>
        </div>
      </div>
    )}

    {certModal && (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setCertModal(null); setImgLoaded(false); }} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <button onClick={() => { setCertModal(null); setImgLoaded(false); }} className="absolute top-3 right-3 z-10 text-slate-400 hover:text-slate-700 bg-white rounded-full p-1 shadow transition-colors">
            <LuX size={18} />
          </button>
          <div className="flex-1 overflow-auto flex items-center justify-center bg-slate-50 rounded-t-2xl relative" style={{ minHeight: '200px' }}>
            {certModal.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i) ? (
              <>
                {!imgLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
                    <LuLoader size={32} className="animate-spin text-slate-400" />
                  </div>
                )}
                <img
                  src={certModal}
                  alt="Certificate"
                  decoding="async"
                  className={`max-w-full max-h-[75vh] object-contain transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImgLoaded(true)}
                />
              </>
            ) : (
              <iframe src={certModal} title="Certificate" className="w-full h-[75vh] rounded-t-2xl" />
            )}
          </div>
          <div className="px-5 py-3 border-t border-slate-100 flex justify-center bg-white rounded-b-2xl">
            <a href={certModal} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm font-medium hover:underline">View original file ↗</a>
          </div>
        </div>
      </div>
    )}

    </>
  );
};
